import React, {useEffect, useRef, useState} from 'react';
import {useBlocker, useNavigate} from 'react-router-dom';
import {Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import LockIcon from '@mui/icons-material/Lock';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {PageTitle, RecordMessage, RecordPage, WideButtonRow} from '../../shared/components/record/RecordKit';
import {SecondaryActionButton} from '../../shared/components/list-page/ListPageKit';
import {getEstimateFollowUpSchedule, saveEstimateFollowUpSchedule} from '../../shared/services/company-settings.service';
import {EstimateFollowUpSchedule} from '../../shared/dtos/company-settings.dto';
import {SavaquanHttpError} from '../../shared/utils/http-error';
import {logError} from '../../shared/services/logger.service';
import {isAuthRedirectError} from '../../shared/services/auth.service';
import './FollowUpsPage.css';

/*
 * SQ-603: Automatic estimate follow-ups, in Android's design
 * (estimatebuilder/followups/FollowUpsScreen.kt, FollowUpsViewModel.kt; SQ-563).
 * The company's estimate follow-ups: one switch, up to five follow-ups (days
 * after the estimate is sent, 1-60, each later than the one before) and how
 * each goes out (Email; Text is locked until texting is built). Nothing
 * commits until Save changes. Opened from the Estimates list's "Set
 * automatic follow-ups" (SQ-546); the page's name is its own title, with
 * Save changes beside it — nothing is drawn in the green bar and there is no
 * back arrow (SQ-574: the menu's Estimates, or the browser's / phone's own
 * Back, go back). On a wide page each follow-up is one line.
 */

const MAX_FOLLOW_UPS = 5;
const MAX_DAYS = 60;
const TEXT_NOTE_MS = 4000;
const SAVED_FLASH_MS = 3000;
const TEXT_NOT_READY = "Text follow-ups aren't set up yet.";

const NOTE_EMPTY = "Set reminders that go out on their own after you send an estimate, so a client who hasn't gotten back to you hears from you again without you having to remember.";
const NOTE_OFF = 'Your follow-ups are turned off. Nothing goes out on any estimate until you switch them back on.';
const NOTE_ON = 'Counted from the day the estimate is sent. Pick how you want to reach them each time. Reminders stop on their own as soon as the customer approves the estimate or it gets canceled.';
const FOOTER = 'Changes only affect estimates sent from now on. Follow-ups already scheduled on estimates you have sent stay as they were. Switching follow-ups off stops those too.';

const dayLabel = (d: number) => d === 0 ? '0 days' : `${d} ${d === 1 ? 'day' : 'days'} after`;

interface Row {days: number; email: boolean}
interface FollowUpsForm {on: boolean; rows: Row[]}

const EMPTY_FORM: FollowUpsForm = {on: true, rows: []};

const formFromDto = (dto: EstimateFollowUpSchedule | null | undefined): FollowUpsForm =>
  !dto ? EMPTY_FORM : {
    on: dto.enabled !== false,
    rows: dto.delays.slice(0, dto.followUpCount).map(days => ({days, email: true})),
  };

// The server has no per-row method, so a row with Email unchecked is dropped
// rather than saved and silently re-checked on reload.
const dtoFromForm = (form: FollowUpsForm): EstimateFollowUpSchedule => {
  const kept = form.rows.filter(r => r.email);
  return {
    followUpCount: kept.length,
    delays: kept.map(r => r.days),
    // `enabled` is sent exactly as the switch reads: an explicit "off" must
    // reach the server even with zero kept rows (already-booked reminders
    // check this at send time, so coercing it to true would silently
    // resurrect them). The dead end that would otherwise create — rows
    // empty and off, with the switch itself hidden while empty — is kept
    // escapable by always offering "Add a follow-up" while empty (below),
    // not by rewriting what the user asked to save.
    enabled: form.on,
  };
};

const sameForm = (a: FollowUpsForm, b: FollowUpsForm) => JSON.stringify(a) === JSON.stringify(b);

// `savaquanFetch` reports a network failure as a SavaquanHttpError with
// statusCode 0 (not a thrown TypeError), and keeps the server's own reason
// (e.g. "Each follow-up is 1 to 60 days after sending.") in `details`/`message`.
// `load`'s own catch handles its 403 (full-page `forbidden` message) before
// this ever runs, so only `save` needs a 403 case here.
const describeFailure = (e: unknown, action: 'load' | 'save'): string => {
  if (!(e instanceof SavaquanHttpError) || e.statusCode === 0) {
    return `Couldn't ${action} your follow-ups. Check your connection and try again.`;
  }
  if (action === 'save' && e.statusCode === 403) {
    return "Your access level can't change follow-ups.";
  }
  return e.details || e.message || `Couldn't ${action} your follow-ups (HTTP ${e.statusCode}). Try again.`;
};

/** FollowUpsViewModel: each follow-up lands later than the one above it; Email must be checked (Text isn't offered yet). */
// The earlier row a given one must land after. While `on`, that's always the
// literal row above (Save requires every row's Email checked before it'll go
// through, so none of them can be dropped). While off, a row with Email
// unchecked is dropped from the DTO entirely (see dtoFromForm) and never
// reaches the server, so it's skipped when looking for what a later row must
// exceed — its stale day value shouldn't gate a row that will actually be sent.
const priorRow = (rows: Row[], i: number, on: boolean): {index: number; days: number} | null => {
  for (let k = i - 1; k >= 0; k--) {
    if (on || rows[k].email) return {index: k, days: rows[k].days};
  }
  return null;
};
// Days are a structural constraint (the PUT refuses them outside 1-60 / out of
// order whatever `enabled` is) so a row that will actually be submitted is
// checked in every state; the method check only matters while on.
const badDays = (rows: Row[], i: number, on: boolean): boolean =>
  (on || rows[i].email) && rows[i].days <= (priorRow(rows, i, on)?.days ?? 0);
const badMethod = (rows: Row[], i: number) => !rows[i].email;
const cardMessages = (rows: Row[], i: number, on: boolean): string[] => {
  const prior = priorRow(rows, i, on);
  return [
    ...(badDays(rows, i, on) ? [prior === null ? 'Pick how many days.' : `Needs more days than follow-up ${prior.index + 1}.`] : []),
    ...(on && badMethod(rows, i) ? ['Check Email so this one goes out.'] : []),
  ];
};
const hasProblems = (form: FollowUpsForm) => form.rows.some((_, i) => cardMessages(form.rows, i, form.on).length > 0);

const Toggle = ({on}: {on: boolean}) => (
  <span className={on ? 'fu-toggle fu-toggle--on' : 'fu-toggle'} aria-hidden="true"><span/></span>
);

const MethodBox = ({label, checked, on, wrong, locked, onClick}: {
  label: string; checked: boolean; on: boolean; wrong: boolean; locked: boolean; onClick: () => void;
}) => (
  <button type="button" className={['fu-method', checked && on ? 'fu-method--checked' : '', !on || locked ? 'fu-method--faint' : ''].join(' ').trim()}
          role="checkbox" aria-checked={checked} aria-disabled={!on} onClick={() => on && onClick()}>
    <span className={['fu-box', checked ? (on ? 'fu-box--on' : 'fu-box--off') : '', wrong && !checked ? 'fu-box--wrong' : ''].join(' ').trim()}>
      {checked ? <CheckIcon/> : locked ? <LockIcon className="fu-lock"/> : null}
    </span>
    {label}
  </button>
);

const DayPicker = ({selected, onPick}: {selected: number; onPick: (d: number) => void}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const row = ref.current?.children[Math.max(0, selected - 2)] as HTMLElement | undefined;
    if (ref.current && row) ref.current.scrollTop = row.offsetTop - (ref.current.firstElementChild as HTMLElement).offsetTop;
  }, [selected]);
  return (
    <div ref={ref} className="fu-picker" role="radiogroup" aria-label="Days after the estimate is sent">
      {Array.from({length: MAX_DAYS + 1}, (_, d) => (
        <button key={d} type="button" className={d === selected ? 'fu-pick fu-pick--on' : 'fu-pick'} role="radio" aria-checked={d === selected} onClick={() => onPick(d)}>
          <span className={d === selected ? 'fu-radio fu-radio--on' : 'fu-radio'}/>{dayLabel(d)}
        </button>
      ))}
    </div>
  );
};

const FollowUpCard = ({index, row, on, tried, rows, pickerOpen, textAsked, onTogglePicker, onPick, onToggleEmail, onTapText, onDelete}: {
  index: number; row: Row; on: boolean; tried: boolean; rows: Row[]; pickerOpen: boolean; textAsked: boolean;
  onTogglePicker: () => void; onPick: (d: number) => void; onToggleEmail: () => void; onTapText: () => void; onDelete: () => void;
}) => {
  const dayWrong = tried && badDays(rows, index, on);
  const methodWrong = on && tried && badMethod(rows, index);
  const messages = tried ? cardMessages(rows, index, on) : [];
  const edge = !on ? 'fu-card--off' : dayWrong || methodWrong ? 'fu-card--wrong' : '';
  return (
    <div className={`fu-card ${edge}`.trim()} aria-label={`Follow-up ${index + 1}`}>
      <div className="fu-card-main">
        <div className="fu-card-head">
          <span className={on ? 'fu-follow' : 'fu-follow fu-follow--off'}>Follow up</span>
          <button type="button" className={['fu-day', dayWrong ? 'fu-day--wrong' : '', !on || row.days === 0 ? 'fu-day--faint' : ''].join(' ').trim()}
                  onClick={() => on && onTogglePicker()} aria-expanded={pickerOpen} disabled={!on}>
            {dayLabel(row.days)}{on && <KeyboardArrowDownIcon/>}
          </button>
        </div>
        <div className="fu-methods">
          <MethodBox label="Text" checked={false} on={on} wrong={false} locked onClick={onTapText}/>
          <MethodBox label="Email" checked={row.email} on={on} wrong={methodWrong} locked={false} onClick={onToggleEmail}/>
        </div>
        {on && (
          <button type="button" className="fu-delete" onClick={onDelete} aria-label={`Delete follow-up ${index + 1}`}>
            <DeleteIcon/>
          </button>
        )}
      </div>
      {pickerOpen && <DayPicker selected={row.days} onPick={onPick}/>}
      {on && textAsked && <div className="fu-card-note">{TEXT_NOT_READY}</div>}
      {messages.length > 0 && <div className="fu-card-messages">{messages.map(m => <div key={m}>{m}</div>)}</div>}
    </div>
  );
};

export const FollowUpsPage: React.FC = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const loadFailed = loadError !== null;
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [saved, setSaved] = useState<FollowUpsForm | null>(null);
  const [pending, setPending] = useState<FollowUpsForm | null>(null);
  const [tried, setTried] = useState(false);
  const [picking, setPicking] = useState<number | null>(null);
  const [textAsked, setTextAsked] = useState<Record<number, number>>({});

  // So save() can tell, after its request answers, whether an edit arrived
  // while it was out — reading `pending` through a ref (kept current on
  // every render) rather than through the setPending updater, whose
  // callback is not guaranteed to run before save() reads the result.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  const flashTimer = useRef<number | null>(null);
  const noteTimers = useRef<number[]>([]);
  useEffect(() => () => {
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    noteTimers.current.forEach(t => window.clearTimeout(t));
  }, []);

  const load = () => {
    setIsLoading(true);
    setForbidden(false);
    setLoadError(null);
    getEstimateFollowUpSchedule(navigate)
      .then(dto => {
        const form = formFromDto(dto?.value);
        setSaved(form);
        setPending(form);
      })
      .catch(e => {
        if (isAuthRedirectError(e)) return;
        if (e instanceof SavaquanHttpError && e.statusCode === 403) {
          setForbidden(true);
          return;
        }
        logError('Error loading follow-up schedule', {}, e instanceof Error ? e : undefined);
        setLoadError(describeFailure(e, 'load'));
      })
      .finally(() => setIsLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const isDirty = !isLoading && !forbidden && !loadFailed && !!saved && !!pending && !sameForm(saved, pending);
  const empty = pending?.rows.length === 0;

  // Leaving with unsaved changes (a menu item, the browser's or phone's Back) asks first.
  const blocker = useBlocker(() => isDirty);
  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const edit = (change: (f: FollowUpsForm) => FollowUpsForm) => {
    setPending(current => current ? change(current) : current);
    setJustSaved(false);
    setSaveError(null);
  };

  const discard = () => {
    setPending(saved);
    setTried(false);
  };

  const save = async () => {
    if (!pending || isSaving || isLoading || forbidden || loadFailed) return;
    setTried(true);
    if (hasProblems(pending)) return;
    setIsSaving(true);
    setSaveError(null);
    const requested = pending;
    try {
      const response = await saveEstimateFollowUpSchedule(navigate, dtoFromForm(requested));
      const stored = formFromDto(response.value);
      // Value compare, like Android's `it.pending == pending`: an edit while
      // the PUT was out (toggle off and back on) can leave an equal-but-new
      // object in `pendingRef`, which `===` would wrongly treat as a newer edit.
      const stillLatest = !!pendingRef.current && sameForm(pendingRef.current, requested);
      setSaved(stored);
      if (stillLatest) setPending(stored);
      setTried(false);
      setJustSaved(stillLatest);
      if (stillLatest) {
        if (flashTimer.current) window.clearTimeout(flashTimer.current);
        flashTimer.current = window.setTimeout(() => setJustSaved(false), SAVED_FLASH_MS);
      }
    } catch (e) {
      if (isAuthRedirectError(e)) return;
      logError('Error saving follow-up schedule', {}, e instanceof Error ? e : undefined);
      setSaveError(describeFailure(e, 'save'));
    } finally {
      setIsSaving(false);
    }
  };

  const addRow = () => {
    if (!pending || pending.rows.length >= MAX_FOLLOW_UPS) return;
    setPicking(null);
    // Android: a new card starts at 0 days with nothing checked, switches the page on, and quiets the last save's complaints.
    edit(f => ({on: true, rows: [...f.rows, {days: 0, email: false}]}));
    setTried(false);
  };

  const removeRow = (i: number) => {
    setPicking(null);
    setTextAsked(t => {
      const next: Record<number, number> = {};
      Object.entries(t).forEach(([k, v]) => {
        const idx = Number(k);
        if (idx !== i) next[idx > i ? idx - 1 : idx] = v;
      });
      return next;
    });
    if (pending && pending.rows.length <= 1) setTried(false);
    edit(f => ({...f, rows: f.rows.filter((_, k) => k !== i)}));
  };

  const tapText = (i: number) => {
    const at = Date.now();
    setTextAsked(t => ({...t, [i]: at}));
    const timer = window.setTimeout(() => {
      noteTimers.current = noteTimers.current.filter(id => id !== timer);
      // `removeRow` re-keys `textAsked` when a row above is deleted, so the
      // note can be at a different key than `i` by the time this fires — find
      // it by the timestamp, not the index this closure was created with.
      setTextAsked(t => {
        const entry = Object.entries(t).find(([, v]) => v === at);
        if (!entry) return t;
        const {[Number(entry[0])]: _removed, ...rest} = t;
        return rest;
      });
    }, TEXT_NOTE_MS);
    noteTimers.current.push(timer);
  };

  return (
    <RecordPage name="FollowUpsPage">
      <div className="fu-page">
        {isLoading ? <CircularProgress/> : forbidden ? (
          <RecordMessage error text="Your access level can't change automatic follow-ups. Ask an owner or admin."/>
        ) : loadFailed ? (
          <>
            <RecordMessage error text={loadError ?? "Couldn't load your follow-ups."}/>
            <SecondaryActionButton label="Try again" onClick={load}/>
          </>
        ) : pending && (
          <>
            <PageTitle title="Automatic estimate follow‑ups">
              {(!empty || isDirty) && (
                <div className="fu-save-row">
                  {(isDirty || justSaved) && <span className={isDirty ? 'fu-saved fu-saved--dirty' : 'fu-saved'}>{isDirty ? 'Unsaved' : 'Saved'}</span>}
                  <button type="button" className="fu-save" disabled={isSaving} onClick={() => void save()}>
                    {isSaving ? <CircularProgress size={16} color="inherit"/> : 'Save changes'}
                  </button>
                </div>
              )}
            </PageTitle>

            {tried && hasProblems(pending) && <div className="fu-error">Fix the follow-ups marked below, then save again.</div>}
            {saveError && <div className="fu-error">{saveError}</div>}

            {!empty && (
              <button type="button" className={pending.on ? 'fu-switch fu-switch--on' : 'fu-switch'} role="switch" aria-checked={pending.on}
                      onClick={() => { setPicking(null); edit(f => ({...f, on: !f.on})); }}>
                <span className="fu-switch-text">
                  <b>Automatic follow-ups</b>
                  <span>{pending.on ? 'On for every estimate you send' : 'Off'}</span>
                </span>
                <Toggle on={pending.on}/>
              </button>
            )}

            <p className="fu-note">{empty ? NOTE_EMPTY : !pending.on ? NOTE_OFF : NOTE_ON}</p>

            {pending.rows.length > 0 && <div className="fu-cards">
              {pending.rows.map((row, i) => (
                <FollowUpCard key={i} index={i} row={row} on={pending.on} tried={tried} rows={pending.rows}
                              pickerOpen={pending.on && picking === i} textAsked={!!textAsked[i]}
                              onTogglePicker={() => setPicking(p => p === i ? null : i)}
                              onPick={d => { edit(f => ({...f, rows: f.rows.map((r, k) => k === i ? {...r, days: d} : r)})); setPicking(null); }}
                              onToggleEmail={() => edit(f => ({...f, rows: f.rows.map((r, k) => k === i ? {...r, email: !r.email} : r)}))}
                              onTapText={() => tapText(i)}
                              onDelete={() => removeRow(i)}/>
              ))}
            </div>}

            {/* Off+empty would otherwise hide both the switch (gated on !empty)
              * and this (gated on `on`), with no way back in. addRow already
              * turns the switch on as a side effect, so offering Add here too
              * is the escape hatch, not a switch rewritten on the user's behalf. */}
            {(pending.on || empty) && (pending.rows.length < MAX_FOLLOW_UPS
              ? <WideButtonRow>
                  <button type="button" className="fu-add" onClick={addRow}>
                    {pending.rows.length === 0 ? 'Add a follow-up' : 'Add another follow-up'}
                  </button>
                </WideButtonRow>
              : <p className="fu-note">Five follow-ups is the most an estimate can have.</p>)}

            {!empty && <p className="fu-footer">{FOOTER}</p>}
          </>
        )}
      </div>

      <Dialog open={blocker.state === 'blocked'} onClose={() => blocker.state === 'blocked' && blocker.reset()}>
        <DialogTitle>Leave without saving?</DialogTitle>
        <DialogContent>
          <DialogContentText>Your follow-up changes have not been saved.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => blocker.state === 'blocked' && blocker.reset()}>Keep editing</Button>
          <Button color="error" onClick={() => {
            discard();
            if (blocker.state === 'blocked') blocker.proceed();
          }}>Discard changes</Button>
        </DialogActions>
      </Dialog>
    </RecordPage>
  );
};

export default FollowUpsPage;
